// Canonical effort-scale layer. The whole app pivots on ONE internal
// effort scale; the other is a pure read-time projection of it. This
// module is the single source of truth for that relationship so a stat
// aggregate never mixes a raw RPE from one set with a raw RIR from
// another (a garbage average — RPE 8 and RIR 2 are the SAME effort but
// opposite numbers).
//
// Definition (modern powerlifting, the one Antenore is learning):
//
//     RPE = 10 − RIR        RIR = 10 − RPE
//
// So RIR 2 ("two reps left in the tank") == RPE 8. Both run 0..10:
// RIR 0 / RPE 10 = total failure; RIR 10 / RPE 0 = trivially easy.
//
// Canonical internal unit = RIR (more concrete to a beginner: "how many
// reps did I have left?"). RPE is exposed only as a display projection,
// fixed to the RIR-based definition above so the conversion stays exact.

// The two scales JL understands. RIR is the canonical/default; RPE is the
// alternative display projection.
export const EFFORT_SCALES = ["rir", "rpe"] as const;
export type EffortScale = (typeof EFFORT_SCALES)[number];

// Both scales share the same 0..10 domain.
export const EFFORT_SCALE_MAX = 10;

// The RIR at/above which a set counts as "submaximal" — reps left in
// reserve, so it under-states true capacity. Canonical effort threshold
// shared by the analytics that must tell a near-failure ("hard") set from
// an easy one: the hard-set hypertrophy accumulator counts sets at RIR ≤
// this (AP-2), and the regression detector downgrades a decline built on
// RIR ≥ this to a plateau (an easy set's e1RM can't prove a lower ceiling).
// RIR 2 == RPE 8. Reuse this everywhere — never re-pick a threshold inline.
export const EFFORT_SUBMAXIMAL_RIR = 2;

// A set's EFFECTIVE RIR — the ONE canonical effort resolution, folding the
// two rules that used to live apart (MX-4):
//   1. fail ⇒ RIR 0. A failed set was taken to (or past) failure, so it
//      counts as maximal effort — the established convention (maxRegression's
//      ceiling-attempt gate + the AP-0p fail model: "fail is exactly the
//      maximal-effort signal").
//   2. rir ?? rpeToRir(rpe). RIR is the canonical unit, but legacy rows may
//      carry ONLY rpe (pre-single-column sets, and Leonardo logs in the RPE
//      scale). When rir is absent, derive it from rpe instead of dropping the
//      effort — never require both fields. rpe is a projection of rir
//      (effortScale.ts), so this recovers the real datum, it doesn't invent one.
// Returns null only when effort is genuinely unknown (no fail, no rir, no rpe)
// so a caller can tell an easy set from an untagged one. This is the single
// source for both rules; every effort reader (hard-set accumulator ≤ 2,
// ceiling attempt === 0, topSetRir, drift) derives from it, never re-tests
// `fail` or re-derives the rpe fallback inline.
export const effectiveRir = (set: {
  fail?: boolean;
  rir?: number | null;
  rpe?: number | null;
}): number | null => {
  if (set.fail === true) return 0;
  if (set.rir != null) return set.rir;
  if (set.rpe != null) return rpeToRir(set.rpe);
  return null;
};

const clampEffort = (v: number): number =>
  Math.min(EFFORT_SCALE_MAX, Math.max(0, v));

// RPE → RIR and back. Clamped so an out-of-range input (a typo, a stale
// value) can never produce a negative or >10 effort downstream.
export const rpeToRir = (rpe: number): number => clampEffort(EFFORT_SCALE_MAX - rpe);
export const rirToRpe = (rir: number): number => clampEffort(EFFORT_SCALE_MAX - rir);

// Convert a value between scales. Identity when from === to. Used by the
// Logger to render a prescribed target (authored in either scale) in the
// athlete's preferred scale, and by the save path to derive the mirror
// field so a set always carries both rpe and rir in lockstep.
export const convertEffort = (
  value: number,
  from: EffortScale,
  to: EffortScale,
): number => {
  if (from === to) return value;
  return from === "rpe" ? rpeToRir(value) : rirToRpe(value);
};
